/**
 * StaffLicenses — IT-SCE Intern: Lizenzen + Downloads-Releases.
 *
 * TQ #9699 (Staff-Migration Teil F, Sub-Task aus #9523). Portiert aus
 * customer-portal StaffLicenses.tsx + StaffDownloads.tsx in eine Seite mit
 * 2 Tabs (der Sidebar-Link "Software-Lizenzen" hat nur einen Slot), Backend
 * unveraendert (portal-api staff_proxy.go, bereits live).
 *
 * Routing: outer-router-level (App.tsx) vor /:slug/*, hinter StaffOnly-Guard.
 * Eigener schlanker Shell (kein CockpitApp-Layout), analog StaffKnowledge.tsx.
 */

import { useEffect, useState } from 'react'
import { api } from '../services/api'
import type { StaffLicense, StaffDownloadProduct, StaffDownloadStats } from '../services/api'

function fmtDate(s?: string): string {
  return s ? new Date(s).toLocaleDateString('de-DE') : '—'
}

function fmtBytes(n?: number): string {
  if (!n) return '—'
  const u = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0
  let v = n
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024
    i++
  }
  return `${v.toFixed(1)} ${u[i]}`
}

type LicenseFilter = 'all' | 'active' | 'expired' | 'revoked' | 'internal'

function LicensesTab() {
  const [licenses, setLicenses] = useState<StaffLicense[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<LicenseFilter>('all')

  const load = () => {
    setLoading(true)
    setError(null)
    api
      .getStaffLicenses()
      .then(setLicenses)
      .catch((err: unknown) => {
        console.error('[StaffLicenses] load failed:', err)
        setError('Lizenzen konnten nicht geladen werden.')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const now = Date.now()
  const filtered = licenses.filter((l) => {
    if (filter === 'revoked' && !l.revoked_at) return false
    if (filter !== 'revoked' && l.revoked_at) return false
    if (filter === 'expired' && (!l.expires_at || new Date(l.expires_at).getTime() > now)) return false
    if (filter === 'active' && (l.revoked_at || (l.expires_at && new Date(l.expires_at).getTime() < now))) return false
    if (filter === 'internal' && !l.is_internal) return false
    if (search) {
      const q = search.toLowerCase()
      return (l.key + ' ' + (l.customer_email ?? '') + ' ' + (l.product_slug ?? '')).toLowerCase().includes(q)
    }
    return true
  })

  const stats = licenses.reduce(
    (acc, l) => {
      if (l.revoked_at) acc.revoked++
      else if (l.expires_at && new Date(l.expires_at).getTime() < now) acc.expired++
      else acc.active++
      if (l.is_internal) acc.internal++
      return acc
    },
    { active: 0, expired: 0, revoked: 0, internal: 0 },
  )

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
        <StatCard label="Gesamt" value={String(licenses.length)} />
        <StatCard label="Aktiv" value={String(stats.active)} color="#10b981" />
        <StatCard label="Abgelaufen" value={String(stats.expired)} color="#f59e0b" />
        <StatCard label="Widerrufen" value={String(stats.revoked)} color="#ef4444" />
        <StatCard label="IT-SCE intern" value={String(stats.internal)} />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Suche Key, Email, Produkt…"
          style={{ flex: 1, minWidth: 200, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6 }}
        />
        {(['all', 'active', 'expired', 'revoked', 'internal'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '6px 12px',
              background: filter === f ? '#FF6B35' : '#fff',
              color: filter === f ? '#fff' : '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: 999,
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            {f === 'all' ? 'Alle' : f === 'active' ? 'Aktiv' : f === 'expired' ? 'Abgelaufen' : f === 'revoked' ? 'Widerrufen' : 'Intern'}
          </button>
        ))}
      </div>

      {loading && <p style={{ color: '#666' }}>Lade Lizenzen…</p>}

      {error && (
        <div style={{ padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#b91c1c' }}>
          {error}{' '}
          <button onClick={load} style={{ marginLeft: 8, textDecoration: 'underline', background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer' }}>
            Erneut versuchen
          </button>
        </div>
      )}

      {!loading && !error && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Key</th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Produkt</th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Kunde</th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Typ</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Seats</th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Läuft ab</th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => {
                const isRevoked = !!l.revoked_at
                const isExpired = !isRevoked && !!l.expires_at && new Date(l.expires_at).getTime() < now
                const status = isRevoked
                  ? { label: 'Widerrufen', bg: '#fee2e2', fg: '#991b1b' }
                  : isExpired
                    ? { label: 'Abgelaufen', bg: '#fef3c7', fg: '#92400e' }
                    : { label: 'Aktiv', bg: '#d1fae5', fg: '#065f46' }
                return (
                  <tr key={l.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <code style={{ fontSize: 11 }}>{l.key.slice(0, 16)}…</code>
                      {l.is_internal && (
                        <span style={{ marginLeft: 6, padding: '1px 6px', background: '#dbeafe', color: '#1e40af', borderRadius: 8, fontSize: 10 }}>
                          intern
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px' }}>{l.product_slug ?? '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{l.customer_email ?? l.customer_id ?? '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{l.license_type ?? 'standard'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>{l.max_seats ?? '∞'}</td>
                    <td style={{ padding: '10px 12px' }}>{fmtDate(l.expires_at)}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ padding: '2px 8px', background: status.bg, color: status.fg, borderRadius: 12, fontSize: 12 }}>
                        {status.label}
                      </span>
                    </td>
                  </tr>
                )
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>
                    Keine Lizenzen gefunden.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function DownloadsTab() {
  const [products, setProducts] = useState<StaffDownloadProduct[]>([])
  const [stats, setStats] = useState<StaffDownloadStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const load = () => {
    setLoading(true)
    setError(null)
    Promise.all([api.getStaffDownloadProducts(), api.getStaffDownloadStats()])
      .then(([p, s]) => {
        setProducts(p)
        setStats(s)
      })
      .catch((err: unknown) => {
        console.error('[StaffLicenses/Downloads] load failed:', err)
        setError('Downloads konnten nicht geladen werden.')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filtered = products.filter((p) => {
    if (!search) return true
    const q = search.toLowerCase()
    return (p.name + ' ' + p.slug + ' ' + (p.description ?? '')).toLowerCase().includes(q)
  })

  return (
    <div>
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
          <StatCard label="Produkte" value={String(stats.total_products ?? products.length)} />
          <StatCard label="Releases" value={String(stats.total_releases ?? '—')} />
          <StatCard label="Downloads gesamt" value={String(stats.total_downloads ?? '—')} />
          {stats.recent_downloads_24h != null && <StatCard label="Letzte 24h" value={String(stats.recent_downloads_24h)} color="#10b981" />}
          {stats.total_storage_bytes && <StatCard label="Storage" value={fmtBytes(stats.total_storage_bytes)} />}
        </div>
      )}

      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Suche Produkt…"
        style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 16 }}
      />

      {loading && <p style={{ color: '#666' }}>Lade Produkte…</p>}

      {error && (
        <div style={{ padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#b91c1c' }}>
          {error}{' '}
          <button onClick={load} style={{ marginLeft: 8, textDecoration: 'underline', background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer' }}>
            Erneut versuchen
          </button>
        </div>
      )}

      {!loading && !error && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {filtered.map((p) => (
            <div key={p.id} style={{ padding: 16, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{p.name}</div>
                  <code style={{ fontSize: 12, color: '#6b7280' }}>{p.slug}</code>
                </div>
                {p.product_type && (
                  <span style={{ padding: '2px 8px', background: '#f3f4f6', color: '#374151', borderRadius: 12, fontSize: 11 }}>{p.product_type}</span>
                )}
              </div>
              {p.description && <div style={{ color: '#6b7280', fontSize: 13, marginBottom: 8 }}>{p.description}</div>}
              <div style={{ color: '#9ca3af', fontSize: 12 }}>
                {p.total_releases != null && (
                  <>
                    {p.total_releases} Release{p.total_releases === 1 ? '' : 's'}
                  </>
                )}
                {p.total_downloads != null && (
                  <>
                    {' '}
                    · {p.total_downloads} Download{p.total_downloads === 1 ? '' : 's'}
                  </>
                )}
                {p.is_public === false && (
                  <>
                    {' '}
                    · <span style={{ color: '#92400e' }}>privat</span>
                  </>
                )}
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div style={{ gridColumn: '1/-1', padding: 32, textAlign: 'center', color: '#9ca3af' }}>Keine Produkte gefunden.</div>}
        </div>
      )}
    </div>
  )
}

function StatCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ padding: 14, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
      <div style={{ color: '#6b7280', fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: color ?? '#111827' }}>{value}</div>
    </div>
  )
}

export default function StaffLicenses() {
  const [tab, setTab] = useState<'licenses' | 'downloads'>('licenses')

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <a href="/" style={{ color: '#FF6B35', textDecoration: 'none', fontSize: '0.9rem' }}>
          ← Cockpit
        </a>
      </div>
      <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.6rem' }}>💾 Software-Lizenzen</h1>
      <p style={{ color: '#666', marginTop: 0, marginBottom: '1rem', fontSize: '0.9rem' }}>
        Lizenzen aller Kunden + Downloads-Releases (MCP-Server, Hub-Builds, AI-Desktop).
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem', borderBottom: '1px solid #e5e7eb' }}>
        <button
          onClick={() => setTab('licenses')}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderBottom: tab === 'licenses' ? '2px solid #FF6B35' : '2px solid transparent',
            background: 'none',
            color: tab === 'licenses' ? '#FF6B35' : '#6b7280',
            fontWeight: tab === 'licenses' ? 600 : 400,
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          📜 Lizenzen
        </button>
        <button
          onClick={() => setTab('downloads')}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderBottom: tab === 'downloads' ? '2px solid #FF6B35' : '2px solid transparent',
            background: 'none',
            color: tab === 'downloads' ? '#FF6B35' : '#6b7280',
            fontWeight: tab === 'downloads' ? 600 : 400,
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          📦 Downloads-Releases
        </button>
      </div>

      {tab === 'licenses' ? <LicensesTab /> : <DownloadsTab />}
    </div>
  )
}
